import React from "react";
import { Box, Typography } from "@mui/material";
import FieldText from "./FieldText";
import FieldNumber from "./FieldNumber";
import FieldBoolean from "./FieldBoolean";
import FieldSelect from "./FieldSelect";
import FieldDate from "./FieldDate";
import FieldArray from "./FieldArray";

export default function FieldObject({
  name,
  fields = {},
  getValue = () => undefined,
  onChange = () => {},
  editMode = true,
  // Optional flat error map: { 'a.b.c': 'message' }
  errors,
  // Path array to this object's location in the schema
  path = [],
}) {
  const innerKeys = Object.keys(fields || {});

  const humanize = (key) => {
    if (!key) return "";
    return key
      .toString()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const renderInner = (ik) => {
    const idef = fields[ik] || {};
    const itype = idef.type;
    const value = getValue ? getValue(ik) : undefined;
    const fullPath = [...path, ik].join(".");
    const errorText =
      errors && fullPath in errors ? errors[fullPath] : undefined;

    const handle = (v) => {
      console.log(`[FieldObject] Updating field "${ik}" with value:`, v);
      onChange(ik, v);
    };

    // Support nested object fields by opening and rendering their children
    if (itype === "object" && idef.fields) {
      const current =
        value && typeof value === "object" && !Array.isArray(value)
          ? value
          : {};
      return (
        <Box key={ik} mb={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                minWidth: { xs: 110, sm: 150, md: 200 },
                fontSize: { xs: "0.875rem", sm: "0.9rem" },
              }}
            >
              {humanize(ik)}:
            </Typography>
            {!editMode && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
              >
                {/* Object label removed for cleaner view */}
              </Typography>
            )}
          </Box>
          {idef.description && (
            <Typography variant="caption" color="text.secondary">
              {idef.description}
            </Typography>
          )}
          <Box sx={{ pl: 3, mt: 0.5 }}>
            <FieldObject
              name={ik}
              fields={idef.fields}
              editMode={editMode}
              errors={errors}
              path={[...path, ik]}
              getValue={(jk) =>
                current && Object.prototype.hasOwnProperty.call(current, jk)
                  ? current[jk]
                  : undefined
              }
              onChange={(jk, v) =>
                onChange(ik, {
                  ...current,
                  [jk]: v,
                })
              }
            />
          </Box>
        </Box>
      );
    }

    // Support nested array-of-object fields
    if (itype === "array" && idef.fields) {
      const arrValue = Array.isArray(value) ? value : [];
      return (
        <FieldArray
          key={ik}
          name={ik}
          schema={idef}
          value={arrValue}
          onChange={(next) => handle(next)}
        />
      );
    }

    if (itype === "number") {
      if (editMode) {
        return (
          <FieldNumber
            key={ik}
            name={ik}
            schema={idef}
            value={value}
            onChange={handle}
          />
        );
      }
      // View mode
      return (
        <Box key={ik} mb={1} sx={{ maxWidth: 360 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: { xs: "0.875rem", sm: "0.9rem" },
                minWidth: { xs: 110, sm: 150, md: 200 },
              }}
            >
              {humanize(ik)}:
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
            >
              {value !== null && value !== undefined && value !== ""
                ? value
                : "—"}
            </Typography>
          </Box>
          {idef.description && (
            <Typography variant="caption" color="text.secondary">
              {idef.description}
            </Typography>
          )}
        </Box>
      );
    }

    if (itype === "string") {
      if (editMode) {
        return (
          <FieldText
            key={ik}
            name={ik}
            schema={idef}
            value={value}
            onChange={handle}
            errorText={errorText}
          />
        );
      }
      // View mode
      return (
        <Box key={ik} mb={1} sx={{ maxWidth: 360 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: { xs: "0.875rem", sm: "0.9rem" },
                minWidth: { xs: 110, sm: 150, md: 200 },
              }}
            >
              {humanize(ik)}:
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
            >
              {value !== null && value !== undefined && value !== ""
                ? value
                : "—"}
            </Typography>
          </Box>
          {idef.description && (
            <Typography variant="caption" color="text.secondary">
              {idef.description}
            </Typography>
          )}
        </Box>
      );
    }

    if (itype === "boolean") {
      if (editMode) {
        return (
          <FieldBoolean
            key={ik}
            name={ik}
            schema={idef}
            value={value}
            onChange={handle}
          />
        );
      }
      // View mode
      return (
        <Box key={ik} mb={1} sx={{ maxWidth: 360 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: { xs: "0.875rem", sm: "0.9rem" },
                minWidth: { xs: 110, sm: 150, md: 200 },
              }}
            >
              {humanize(ik)}:
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
            >
              {value !== null && value !== undefined
                ? value
                  ? "Yes"
                  : "No"
                : "—"}
            </Typography>
          </Box>
          {idef.description && (
            <Typography variant="caption" color="text.secondary">
              {idef.description}
            </Typography>
          )}
        </Box>
      );
    }

    if (itype === "select") {
      if (editMode) {
        return (
          <FieldSelect
            key={ik}
            name={ik}
            schema={idef}
            value={value}
            onChange={handle}
          />
        );
      }
      // View mode
      return (
        <Box key={ik} mb={1} sx={{ maxWidth: 360 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: { xs: "0.875rem", sm: "0.9rem" },
                minWidth: { xs: 110, sm: 150, md: 200 },
              }}
            >
              {humanize(ik)}:
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
            >
              {value !== null && value !== undefined && value !== ""
                ? humanize(value)
                : "—"}
            </Typography>
          </Box>
          {idef.description && (
            <Typography variant="caption" color="text.secondary">
              {idef.description}
            </Typography>
          )}
        </Box>
      );
    }

    if (itype === "date") {
      if (editMode) {
        return (
          <FieldDate
            key={ik}
            name={ik}
            schema={idef}
            value={value}
            onChange={handle}
          />
        );
      }
      // View mode
      return (
        <Box key={ik} mb={1} sx={{ maxWidth: 360 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: { xs: "0.875rem", sm: "0.9rem" },
                minWidth: { xs: 110, sm: 150, md: 200 },
              }}
            >
              {humanize(ik)}:
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
            >
              {value !== null && value !== undefined && value !== ""
                ? value
                : "—"}
            </Typography>
          </Box>
          {idef.description && (
            <Typography variant="caption" color="text.secondary">
              {idef.description}
            </Typography>
          )}
        </Box>
      );
    }

    // fallback UI for unsupported/unknown inner types
    return (
      <Box key={ik} mb={1} sx={{ maxWidth: 360 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
          >
            {humanize(ik)}:
          </Typography>
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
          >
            —
          </Typography>
        </Box>
        {idef.description && (
          <Typography variant="caption" color="text.secondary">
            {idef.description}
          </Typography>
        )}
      </Box>
    );
  };

  return <Box sx={{ pl: 3, mt: 0.5 }}>{innerKeys.map(renderInner)}</Box>;
}
